import { forwardRef } from 'react'
import { clsx } from 'clsx'
import { useResponsive } from '@/contexts/ResponsiveContext'
import LoadingSpinner from './LoadingSpinner'

/**
 * Enhanced Button component with responsive sizing and accessibility
 */
const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  className = '',
  type = 'button',
  onClick,
  ...props
}, ref) => {
  const { isMobile, getButtonSize } = useResponsive()

  // Base button classes
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation'

  // Variant styles
  const variantClasses = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 active:bg-primary-800',
    secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 active:bg-gray-400',
    outline: 'border-2 border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500 active:bg-primary-100',
    ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-500 active:bg-gray-200',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 active:bg-red-800',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 active:bg-green-800',
    warning: 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500 active:bg-yellow-800'
  }

  // Size classes - responsive based on device
  const getSizeClasses = () => {
    if (isMobile) {
      // Mobile-optimized sizes (touch-friendly)
      const mobileSizes = {
        xs: 'h-10 px-3 text-sm min-w-[2.5rem]',
        sm: 'h-11 px-4 text-sm min-w-[3rem]',
        md: 'h-12 px-6 text-base min-w-[3.5rem]',
        lg: 'h-14 px-8 text-lg min-w-[4rem]',
        xl: 'h-16 px-10 text-xl min-w-[4.5rem]'
      }
      return mobileSizes[size] || mobileSizes.md
    } else {
      // Desktop sizes
      const desktopSizes = {
        xs: 'h-8 px-2 text-xs min-w-[2rem]',
        sm: 'h-9 px-3 text-sm min-w-[2.5rem]',
        md: 'h-10 px-4 text-sm min-w-[3rem]',
        lg: 'h-11 px-6 text-base min-w-[3.5rem]',
        xl: 'h-12 px-8 text-lg min-w-[4rem]'
      }
      return desktopSizes[size] || desktopSizes.md
    }
  }

  // Loading state classes
  const loadingClasses = loading ? 'cursor-wait' : ''

  // Full width classes
  const widthClasses = fullWidth ? 'w-full' : ''

  // Combine all classes
  const buttonClasses = clsx(
    baseClasses,
    variantClasses[variant],
    getSizeClasses(),
    loadingClasses,
    widthClasses,
    className
  )

  // Handle click events
  const handleClick = (event) => {
    if (loading || disabled) {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }

  return (
    <button
      ref={ref}
      type={type}
      className={buttonClasses}
      disabled={disabled || loading}
      onClick={handleClick}
      aria-disabled={disabled || loading}
      {...props}
    >
      {/* Left icon */}
      {LeftIcon && !loading && (
        <LeftIcon 
          className={clsx(
            'flex-shrink-0',
            children ? 'mr-2' : '',
            isMobile ? 'h-5 w-5' : 'h-4 w-4'
          )} 
        />
      )}

      {/* Loading spinner */}
      {loading && (
        <LoadingSpinner 
          size={isMobile ? 'md' : 'sm'} 
          className={clsx(
            'text-current',
            children ? 'mr-2' : ''
          )}
        />
      )}

      {/* Button content */}
      {children && (
        <span className={loading ? 'opacity-75' : ''}>
          {children}
        </span>
      )}

      {/* Right icon */}
      {RightIcon && !loading && (
        <RightIcon 
          className={clsx(
            'flex-shrink-0',
            children ? 'ml-2' : '',
            isMobile ? 'h-5 w-5' : 'h-4 w-4'
          )} 
        />
      )}
    </button>
  )
})

Button.displayName = 'Button'

export default Button